import React, { FC } from 'react';
import { Button, Form, InputNumber, Modal, Select } from 'antd';
import { useForm } from 'antd/es/form/Form';
import { mapToMenuWithDivider, renderRequiredMark, rules } from 'common/helpers';
import { ArrowDownShort } from 'common/svg';
import { DEFAULT_VALUE_1, LIST_LIMIT_0, SELECT_LIST_HEIGHT_320 } from 'common/config';
import { useAppDispatch, useAppSelector } from 'app/store/store.hooks';
import { getUserOptions } from 'entities/User/User.helper';
import { getSubdivisionOptions } from 'entities/Subdivision/Subdivision.helper';
import { ICreateInventoryPayload } from 'entities/Inventory/Inventory.models';
import { getSubdivisionList, selectSubdivisionList } from 'entities/Subdivision/Subdivision.slice';
import { selectUserList } from 'entities/User/User.slice';

interface IComponentProps {
  open: boolean;
  loading: boolean;
  onCancel: () => void;
  onSubmit: (value: ICreateInventoryPayload, onSuccess: () => void) => void;
}

export const InventoryResponsibleAppointModal: FC<IComponentProps> = ({ open, loading, onCancel, onSubmit }) => {
  const dispatch = useAppDispatch();
  // subdivision
  const subdivisionList = useAppSelector(selectSubdivisionList);
  // user
  const userList = useAppSelector(selectUserList);

  const [form] = useForm();

  const userOptions = getUserOptions(userList);
  const subdivisionOptions = getSubdivisionOptions(subdivisionList);

  const changeUser = async (id: number | null) => {
    form.resetFields(['subdivisionId']);

    const subdivisionList = await dispatch(
      getSubdivisionList({
        userId: id ? id : undefined,
        limit: LIST_LIMIT_0,
      }),
    );

    if (subdivisionList?.length === DEFAULT_VALUE_1) {
      form.setFieldValue('subdivisionId', subdivisionList[0].id);
    }
  };

  const handleCancel = () => {
    if (loading) {
      return;
    }

    form.resetFields();
    onCancel();
  };

  const handleSubmit = (values: ICreateInventoryPayload) => {
    onSubmit(values, handleCancel);
  };

  return (
    <Modal
      rootClassName="redesign modal"
      width={480}
      centered
      open={open}
      onCancel={handleCancel}
      destroyOnClose
      closeIcon={false}
      footer={[
        <Button key="cancel" className="button-sm secondary" onClick={handleCancel} loading={loading}>
          Назад
        </Button>,
        <Button key="submit" className="button-sm primary" onClick={form.submit} loading={loading}>
          Сохранить
        </Button>,
      ]}
    >
      <div className="mb-3 ff-medium fs-m">Назначить ответственного</div>

      <div className="mb-6 ff-regular fs-xs color-dark-grey">
        Выберите ответственного за инструмент. После завершения инструмент перейдет на баланс выбранного сотрудника.
      </div>

      <Form form={form} layout="vertical" requiredMark={renderRequiredMark} onFinish={handleSubmit}>
        <Form.Item label="Ответственный" name="userId" rules={[rules.required()]}>
          <Select
            rootClassName="redesign"
            placeholder="Выберите сотрудника"
            suffixIcon={<ArrowDownShort />}
            listHeight={SELECT_LIST_HEIGHT_320}
            virtual={false}
            options={mapToMenuWithDivider(userOptions)}
            onChange={changeUser}
          />
        </Form.Item>

        <Form.Item label="Подразделение" name="subdivisionId" rules={[rules.required()]}>
          <Select
            rootClassName="redesign"
            placeholder="Выберите подразделение"
            suffixIcon={<ArrowDownShort />}
            listHeight={SELECT_LIST_HEIGHT_320}
            virtual={false}
            options={mapToMenuWithDivider(subdivisionOptions)}
          />
        </Form.Item>

        <Form.Item label="Количество оборудования" name="count" rules={[rules.required()]}>
          <InputNumber suffix="шт" controls={false} />
        </Form.Item>
      </Form>
    </Modal>
  );
};
